import { Eye, Pencil, Search } from 'lucide-react';
import { CHANNELS, GROWTH_STATUS_LABELS } from './channelConfig';
import { isInAppStorefrontEditable } from './inAppDisplayEditable';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function CampaignHistoryTable({
  items,
  loading,
  filters,
  onFiltersChange,
  onView,
  onEditInAppDisplay,
  pagination,
  onPageChange,
}) {
  return (
    <div className="card overflow-hidden p-0">
      <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Rechercher titre ou sujet…"
            value={filters.q || ''}
            onChange={(e) => onFiltersChange({ ...filters, q: e.target.value })}
          />
        </div>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          value={filters.status || ''}
          onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
        >
          <option value="">Tous les statuts</option>
          {Object.entries(GROWTH_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          value={filters.canal || ''}
          onChange={(e) => onFiltersChange({ ...filters, canal: e.target.value })}
        >
          <option value="">Tous les canaux</option>
          {CHANNELS.filter((c) => c.id !== 'PUSH').map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campagne</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Dest.</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Lectures</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">OK / KO</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-500">Chargement…</td>
              </tr>
            )}
            {!loading && (!items || items.length === 0) && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                  Aucune campagne pour le moment.
                </td>
              </tr>
            )}
            {!loading && items?.map((row) => {
              const st = GROWTH_STATUS_LABELS[row.status] || GROWTH_STATUS_LABELS.DRAFT;
              const hasInApp = (row.channels || []).includes('IN_APP');
              const inAppStats = row.stats?.inApp;
              const canEditInApp = isInAppStorefrontEditable(row);
              return (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{row.title || row.subject || '—'}</div>
                    {row.kind === 'TEST' && (
                      <span className="text-xs text-amber-700">Test</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {(row.channels || []).map((c) => (
                        <span key={c} className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                          {CHANNELS.find((x) => x.id === c)?.label || c}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${st.badge}`}>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{row.recipientCount ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">
                    {hasInApp && inAppStats ? (
                      <span title="Lectures In-App">
                        {inAppStats.read ?? 0}
                        {inAppStats.readRatePct != null && (
                          <span className="text-gray-400 text-xs ml-1">({inAppStats.readRatePct} %)</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-gray-400" title="Lecture non mesurée pour l’email seul">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">
                    <span className="text-emerald-700">{row.sentCount ?? 0}</span>
                    {' / '}
                    <span className="text-red-600">{row.failedCount ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center justify-end gap-3">
                      <button
                        type="button"
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
                        onClick={() => onView(row)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Voir
                      </button>
                      {canEditInApp ? (
                        <button
                          type="button"
                          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700"
                          onClick={() => onEditInAppDisplay(row)}
                          data-testid={`edit-in-app-display-${row.id}`}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Modifier l’affichage In-App
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
          <span>
            Page {pagination.page} / {pagination.totalPages} ({pagination.total} campagnes)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-sm py-1"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
            >
              Précédent
            </button>
            <button
              type="button"
              className="btn-secondary text-sm py-1"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => onPageChange(pagination.page + 1)}
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
